import { useEffect, useRef, useState } from 'react';
import { isEmpty } from 'lodash';
import { buildShowMoreLabel } from '../utils/buildShowMoreLabel';
import { EnrichedTagsLayout, Tag } from '../types';
import { getTagDimensions } from '../utils/getTagDimensions';
import { loadIcons } from '../utils/loadIcons';

interface Accumulators {
    width: number;
    height: number;
    lines: number;
    items: number;
    totalItemsToShow: number;
    finishedScanning: boolean;
}

const PARENT_WIDTH_RECALC_THRESHOLD = 10;

/**
 * Initial state for the accumulators
 */
export const ACCUMULATORS_INITIAL_STATE = {
    width: 0,
    height: 0,
    lines: 0,
    items: 0,
    totalItemsToShow: 0,
    finishedScanning: false,
} satisfies Accumulators;

/**
 * Updates accumulators for a new line of tags
 */
export const initNewLine = (accumulators: Accumulators) => {
    accumulators.totalItemsToShow += accumulators.items;
    accumulators.width = 0;
    accumulators.lines += 1;
    accumulators.items = 0;
};

/**
 * Checks if the parent width has changed in more than the threshold since last calculation to recalculate
 * the tags positions
 */
const isParentWidthChanged = (parentWidthCurrent: number, parentWidthPrev: number) =>
    parentWidthCurrent && Math.abs(parentWidthCurrent - parentWidthPrev) >= PARENT_WIDTH_RECALC_THRESHOLD;

interface UseTagsProps {
    tags: Tag[];
    tagsLayout: EnrichedTagsLayout;
    numOfLines: number;
    parentWidth: number;
    linesOffsets?: number[];
    tagsToShowLimit?: number;
}

/**
 * Hook for calculating the tags to show
 * @param tags The tags list that are available in total
 * @param tagsLayout The tags layout configuration
 * @param numOfLines How many lines to show in the UI
 * @param parentWidth The width of the parent element in px (if case 0/non existent, will render none of the tags)
 * @param linesOffsets Array of offsets for each line to where to start the tags (optional)
 * @param tagsToShowLimit Max cap to show tags, even if more fit in the screen (default is no cap)
 */
export const useTags = ({
    tags,
    tagsLayout,
    numOfLines,
    parentWidth,
    linesOffsets,
    tagsToShowLimit = Infinity,
}: UseTagsProps) => {
    const [isLoading, setIsLoading] = useState(true);
    const [shownTags, setShownTags] = useState<Tag[]>([]);
    const parentWidthRef = useRef<number>(0);

    const showAllTags = () => setShownTags(tags);

    useEffect(() => {
        const loadTags = async () => {
            if (isEmpty(tags)) {
                setIsLoading(false);

                return;
            }

            if (!isParentWidthChanged(parentWidth, parentWidthRef.current)) {
                return;
            }

            parentWidthRef.current = parentWidth;

            setIsLoading(true);
            await loadIcons(tags);

            const accumulators = { ...ACCUMULATORS_INITIAL_STATE };

            initNewLine(accumulators);

            for (const tag of tags) {
                const currentLineOffset = linesOffsets?.[accumulators.lines - 1] ?? 0;
                const maxAllowedWidth = parentWidth - currentLineOffset;

                const { icon, label } = tag;
                const { width: tagWidth } = getTagDimensions({
                    tagsLayout,
                    label,
                    icon,
                });

                if (accumulators.width + tagWidth > maxAllowedWidth) {
                    initNewLine(accumulators);

                    if (accumulators.lines > numOfLines) {
                        break;
                    }
                }

                accumulators.width += tagWidth;

                const currentItemsToShow = accumulators.totalItemsToShow + accumulators.items;
                const potentialItemsToHide = tags.length - currentItemsToShow;
                const showMoreLabel = buildShowMoreLabel(potentialItemsToHide);
                const { width: showMoreTagWidth } = getTagDimensions({
                    tagsLayout,
                    label: showMoreLabel,
                    withGap: false,
                });

                if (accumulators.width + showMoreTagWidth > maxAllowedWidth) {
                    initNewLine(accumulators);

                    if (accumulators.lines > numOfLines) {
                        break;
                    }
                }

                accumulators.items++;
            }

            accumulators.totalItemsToShow += accumulators.items;
            accumulators.totalItemsToShow = Math.min(accumulators.totalItemsToShow, tagsToShowLimit);

            setShownTags(tags.slice(0, accumulators.totalItemsToShow));
            setIsLoading(false);
        };

        loadTags();
    }, [tagsLayout, linesOffsets, numOfLines, parentWidth, tags, tagsToShowLimit]);

    return {
        isLoading,
        shownTags,
        showAllTags,
    };
};
